import { GridProps } from '@mui/material';
import { FormikConfig, FormikHelpers, FormikProps } from 'formik';
import React from 'react';
import { IntlShape } from 'react-intl';
import * as yup from 'yup';
import { AnyObjectSchema, Asserts, TypeOf } from 'yup';

export type FormikError = {
    localeId: string;
    values?: Record<string, string | undefined>;
};

type Error = string | FormikError;

// TODO: Find a way to handle `any` in this file
export type FormikErrors<Values> = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [K in keyof Values]?: Values[K] extends Array<any>
        ? Values[K][number] extends object
            ? Array<FormikErrors<Values[K][number]>> | Error | Array<Error>
            : Error | Array<Error>
        : Values[K] extends object
          ? FormikErrors<Values[K]>
          : Error;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type FormikFormType<Values = any> = FormikProps<Values> & {
    validationSchema: AnyObjectSchema;
    validateForm: (values?: Values) => Promise<FormikErrors<Values>>;
    errors: FormikErrors<Values>;
    initialErrors: FormikErrors<Values>;
};

export interface SchemaReturnFunction {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (...args: Array<any>): AnyObjectSchema;
}

type InputSchema = AnyObjectSchema | SchemaReturnFunction;

export type SchemaValues<Schema extends InputSchema> = Schema extends SchemaReturnFunction
    ? TypeOf<ReturnType<Schema>>
    : Schema extends AnyObjectSchema
      ? TypeOf<Schema>
      : never;

export type SchemaSubmitValues<Schema extends InputSchema> = Schema extends SchemaReturnFunction
    ? Asserts<ReturnType<Schema>>
    : Schema extends AnyObjectSchema
      ? Asserts<Schema>
      : never;

export type CustomFormikProps<
    Schema extends AnyObjectSchema,
    Values extends SchemaValues<Schema> = SchemaValues<Schema>
> = Omit<FormikConfig<Values>, 'initialValues' | 'onSubmit' | 'validationSchema'> & {
    initialValues: Values;
    validationSchema: Schema;
    keepId?: boolean;
    keepTypename?: boolean;
    onSubmit: (
        values: SchemaSubmitValues<Schema>,
        formikHelpers: FormikHelpers<Values>
    ) => void | Promise<unknown>;
};

type FieldsToExcludeFromInput =
    | 'value'
    | 'onChange'
    | 'label'
    | 'required'
    | 'error'
    | 'helperText';

export type FormikInputProps<
    InputProps extends Record<string, unknown> | undefined = undefined,
    ToExclude extends keyof InputProps = never
> = Omit<InputProps, FieldsToExcludeFromInput | ToExclude> & {
    /**
     * This "name" string is used:
     * - in the html input.
     * - to access the formik value, error and handler.
     * - to show the corresponding locale string. Then, it must exist a locale string with this key
     * in the localization file of the common package.
     */
    name: string;

    /**
     * Prefix to remove from name to get localeId
     */
    namePrefix?: string;

    /**
     * Optional label to override default taken from name
     */
    label?: React.ReactNode;

    /**
     * Optional localeId to override default taken from name
     */
    localeId?: string;

    /**
     * Formik instance of the form.
     */
    formik: FormikFormType;
    gridProps?: Partial<GridProps>;
    gridClassName?: string;

    intl: IntlShape;
};

export type StringSchema<TType extends string | undefined> = yup.StringSchema<
    TType | null | undefined,
    Record<string, unknown>,
    TType
>;

export type ArraySchema<TType> = yup.ArraySchema<
    yup.AnySchema,
    Record<string, unknown>,
    Array<TType>,
    Array<TType>
>;

export type FunctionSchema<TType, IsOptional extends boolean = true> = yup.BaseSchema<
    IsOptional extends true ? TType | undefined : TType,
    // eslint-disable-next-line @typescript-eslint/no-empty-object-type
    {},
    TType
>;
